/**
 * ProposeGamePage — propose a game at /games/proposals/create.
 *
 * The division's fixtures are pre-built (round-robin), so you pick the GAME (a
 * fixture you're involved in that hasn't been proposed/recorded yet) from a
 * dropdown and offer 1–3 time slots. The opponent accepts a slot (the game is
 * scheduled) or counters. Proposing claims the fixture so nobody else can
 * propose or record the same pairing.
 *
 * A `?gameId=` query param (e.g. from a "Propose" button on the Divisions page)
 * pre-selects that fixture in the dropdown.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { createProposal, fetchAvailableFixtures } from '../../store/slices/gameSlice';
import { fetchLeagueSeason } from '../../store/slices/leagueSlice';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import SlotEditor, {
  emptySlot,
  isSlotComplete,
  isDateOutsideSeason,
  type SlotInput,
} from '../../components/game/SlotEditor';
import FixtureSelectOptions from '../../components/game/FixtureSelectOptions';
import GameNotesField from '../../components/game/GameNotesField';

const ProposeGamePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { availableFixtures, loading } = useSelector((state: RootState) => state.game);
  const { selectedLeagueId } = useSelector((state: RootState) => state.ui);
  const { currentSeason } = useSelector((state: RootState) => state.league);

  // Back/Cancel return to the real (league-scoped) Games page, not the bare
  // /games card list.
  const gamesHref = selectedLeagueId ? `/leagues/${selectedLeagueId}/games` : '/games';

  // A "Propose" button elsewhere (e.g. the Divisions games list) links here with
  // the fixture pre-selected via ?gameId=.
  const [searchParams] = useSearchParams();
  const [selectedGameId, setSelectedGameId] = useState(searchParams.get('gameId') ?? '');
  const [slots, setSlots] = useState<SlotInput[]>([emptySlot()]);
  const [notes, setNotes] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { resolved: tenantResolved } = useActiveLeagueTenant(undefined);

  useEffect(() => {
    if (tenantResolved) {
      dispatch(fetchAvailableFixtures());
    }
  }, [dispatch, tenantResolved]);

  const fixture = useMemo(
    () => availableFixtures.find(f => f.id === selectedGameId),
    [availableFixtures, selectedGameId],
  );

  // Load the fixture's season so its date window can constrain the proposed slots.
  useEffect(() => {
    if (fixture?.seasonId && selectedLeagueId) {
      dispatch(fetchLeagueSeason({ leagueId: selectedLeagueId, seasonId: fixture.seasonId }));
    }
  }, [dispatch, fixture?.seasonId, selectedLeagueId]);

  // Season window as ISO YYYY-MM-DD strings (only when it matches the selected fixture).
  const toISODate = (d: string | Date): string => new Date(d).toISOString().slice(0, 10);
  const seasonBounds = useMemo(() => {
    if (currentSeason && fixture && currentSeason.id === fixture.seasonId) {
      return {
        minDate: toISODate(currentSeason.startDate),
        maxDate: toISODate(currentSeason.endDate),
      };
    }
    return { minDate: undefined, maxDate: undefined };
  }, [currentSeason, fixture]);

  const addSlot = () => {
    if (slots.length < 3) setSlots([...slots, emptySlot()]);
  };
  const removeSlot = (index: number) => {
    if (slots.length > 1) setSlots(slots.filter((_, i) => i !== index));
  };
  const updateSlot = (index: number, slot: SlotInput) => {
    const updated = [...slots];
    updated[index] = slot;
    setSlots(updated);
  };

  const validate = (): string | null => {
    if (!selectedGameId || !fixture) return 'Please select a game.';
    const validSlots = slots.filter(isSlotComplete);
    if (validSlots.length === 0) {
      return 'Each time slot needs a date, time, venue and court/address. Complete the highlighted fields in at least one slot.';
    }
    const { minDate, maxDate } = seasonBounds;
    if (slots.some(s => isDateOutsideSeason(s.date, minDate, maxDate))) {
      return `Every proposed date must fall within the season (${minDate} – ${maxDate}). Fix the highlighted date.`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError(null);

    const validSlots = slots.filter(isSlotComplete);
    try {
      await dispatch(
        createProposal({
          gameId: selectedGameId,
          proposedSlots: validSlots.map(s => ({
            date: s.date,
            time: s.time,
            venue: { name: s.venueName, address: s.venueAddress },
          })),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        }),
      ).unwrap();
      // Land on the real Games page's Proposals tab (not the standalone card page).
      navigate(`${gamesHref}?tab=proposals`);
    } catch (submitErr) {
      setSubmitError(submitErr instanceof Error ? submitErr.message : String(submitErr));
    }
  };

  const isSubmitting = loading;
  const noFixtures = !loading && availableFixtures.length === 0;

  return (
    <div className="max-w-2xl mx-auto">
      <Link to={gamesHref} className="text-sm text-primary-600 hover:text-primary-700">
        &larr; Back to Games
      </Link>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">Propose a game</h1>
      <p className="mt-1 text-gray-600">
        Pick one of your upcoming games and offer one or more time slots to your opponent. They
        accept a slot or counter with their own.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {submitError && (
          <div className="mt-4 rounded-md bg-danger-50 p-4" role="alert">
            <p className="text-sm text-danger-600">{submitError}</p>
          </div>
        )}

        {/* Step 1: Pick the game (fixture) */}
        <section className="mt-6 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">1 · Game</h2>
          <label htmlFor="fixture" className="mt-3 block text-sm font-medium text-gray-700">
            Which game?
          </label>
          <select
            id="fixture"
            value={selectedGameId}
            onChange={e => setSelectedGameId(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
            disabled={loading || noFixtures}
            aria-label="Game to propose"
          >
            <option value="">{loading ? 'Loading games…' : 'Select a game…'}</option>
            <FixtureSelectOptions fixtures={availableFixtures} />
          </select>
          <p className="mt-1 text-xs text-gray-400">
            Only your games that haven't been proposed or recorded yet are listed.
          </p>
          {noFixtures && (
            <p className="mt-1 text-xs text-danger-600">
              You have no games left to propose — they've all been proposed, recorded, or played.
            </p>
          )}
        </section>

        {/* Step 2: Proposed time slots */}
        <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">2 · Proposed time slots</h2>
            <span className="text-xs text-gray-400">Offer up to 3</span>
          </div>

          {slots.map((slot, i) => (
            <SlotEditor
              key={i}
              index={i}
              slot={slot}
              onChange={updated => updateSlot(i, updated)}
              onRemove={slots.length > 1 ? () => removeSlot(i) : undefined}
              showErrors={attemptedSubmit}
              minDate={seasonBounds.minDate}
              maxDate={seasonBounds.maxDate}
            />
          ))}

          {slots.length < 3 && (
            <button
              type="button"
              onClick={addSlot}
              className="mt-3 text-sm text-primary-600 font-medium hover:text-primary-700"
            >
              + Add another slot
            </button>
          )}
        </section>

        {/* Step 3: Notes */}
        <GameNotesField
          label="3 · Notes"
          value={notes}
          onChange={setNotes}
          placeholder="e.g. Prefer weekend evenings; can bring shuttles."
        />

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-3">
          <Link
            to={gamesHref}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || !selectedGameId}
            className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Sending…' : 'Send proposal'}
          </button>
        </div>

        <div className="mt-4 rounded-md bg-primary-50 text-primary-800 text-sm p-3">
          The opponent gets the proposal under <strong>Games &rarr; Proposals</strong>. They can{' '}
          <strong>Accept</strong> a slot (the game is scheduled) or <strong>Counter</strong> with
          their own slots.
        </div>
      </form>
    </div>
  );
};

export default ProposeGamePage;
